import { useEffect, useState } from 'react';
import { IOrganizationMember } from '@vivli/features/organizations/infrastructure/interface';
import { IOrganizationRoles, IUser, IVivliRoleDef } from '@vivli/shared/infrastructure/interface';
import { useOrganizationsApiContext } from '@vivli/core/infrastructure/context';

export function useCurrentMembersHook(organizationId: string, user: IUser = null) {
    const [currentMembers, setCurrentMembers] = useState<IOrganizationMember[]>();
    const [rolesOrgSpecific, setRolesOrgSpecific] = useState<IVivliRoleDef[]>();
    const orgsService = useOrganizationsApiContext();
    const isOrganization = !user;
    function amendMembership(memberIn: IOrganizationMember, membership: IOrganizationRoles) {
        //calculate role-specific columns
        //those are transient columns for displaying in the grid, having names like hasRoleOrgAdmin
        rolesOrgSpecific?.forEach((role) => {
            const colName = `hasRole${role.code}`;
            memberIn[colName] = !!membership?.vivliRoles?.find((roleIn) => roleIn.code === role.code);
        });

        //these are transient columns for membership enabled and notify as Org. Admin
        memberIn['isEnabled'] = !!membership?.isEnabled;
        memberIn['notifyAsOrgAdmin'] = !!membership?.notifyAsOrgAdmin;
    }

    const amendCurrentMembers = (members: IOrganizationMember[]) => {
        const amendedMembers = members.map((memberIn) => {
            memberIn['active'] = memberIn.disabled ? 'Inactive' : 'Active';
            //this is membership for a given organization

            const membership = memberIn.userRole.orgMemberships.find(
                (mem) => (isOrganization && mem.orgId === organizationId) || (!isOrganization && mem.orgId === memberIn.id)
            );
            amendMembership(memberIn, membership);
            return memberIn;
        });
        setCurrentMembers(amendedMembers);
    };
    const { getOrganizationMembers } = useOrganizationsApiContext();

    const getUserMemberships = () => {
        //convert IUser to IOrganizationMember[]
        var members = user.orgMemberships.map((mem) => {
            let userRole = { ...user };
            // include only current organization membership
            userRole.orgMemberships = userRole.orgMemberships.filter((orgMem) => orgMem.orgId == mem.orgId);
            let orgMembership: IOrganizationMember = {
                displayName: 'N/A',
                id: mem.orgId, //we put org id into id - will process displayName based on it later
                email: '',
                userRole: userRole,
                disabled: user.disabled,
                hasAnnotator: false,
                hasDataProvider: false,
                hasIRPApprover: false,
                hasOrgAdmin: false,
                hasQaReviewer: false,
                hasHead: false,
                wasUpdated: false
            };
            return orgMembership;
        });
        amendCurrentMembers(members);
    };

    const getCurrentMembers = () => {
        return getOrganizationMembers(organizationId, true).subscribe((members) => {
            amendCurrentMembers(members);
        });
    };

    useEffect(() => {
        if (isOrganization) {
            const organizationSub = getCurrentMembers();

            return () => {
                organizationSub.unsubscribe();
            };
        } else if (rolesOrgSpecific) {
            getUserMemberships();
        }
    }, [rolesOrgSpecific]);

    useEffect(() => {
        orgsService.getRoles().subscribe((roles) => {
            //SubmissionOwner here to compensate for a wrong flag in the database until we do a migration
            const orgRoles = roles.filter((r) => r.isOrgSpecific || r.code == 'SubmissionOwner');
            setRolesOrgSpecific(orgRoles);
        });
    }, []);

    return {
        currentMembers,
        setCurrentMembers,
        getCurrentMembers,
        amendCurrentMembers,
        rolesOrgSpecific
    };
}
